/**
 * Calendar & Diary — shared Add-Appointment and Reschedule dialogs.
 *
 * Wave 2 extracted these from the wave-1 dashboard widget so the diary page
 * reuses the exact same form, validation, and copy. Behavior (optimistic
 * PATCH, toasts) stays with each host; these are the presentational pieces.
 */
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { Loader2 } from 'lucide-react'
import { api } from '@/api/client'
import type { CalendarEvent } from '@/api/calendar'
import { useToast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { t, type DashboardLocale } from '@/pages/agent/dashboard/copy'
import { resolvedTimezone, toLocalInputValue } from './shared'

export type AddPrefill = { kind: 'meeting' | 'todo'; when: string } | null

/**
 * Wave 6b — the cancel confirmation, shared by the diary and the dashboard
 * widget (both previously inlined an identical dialog). Plain events render
 * the single confirm unchanged. Series targets (recurrence instances) get an
 * explicit scope choice; the host decides what each scope means via
 * onConfirm.
 */
export function CancelEventDialog({
  event,
  locale,
  busy,
  onClose,
  onConfirm,
}: {
  event: CalendarEvent | null
  locale: DashboardLocale
  busy: boolean
  onClose: () => void
  onConfirm: (scope: 'occurrence' | 'series') => void
}) {
  const [scope, setScope] = useState<'occurrence' | 'series'>('occurrence')
  useEffect(() => {
    setScope('occurrence')
  }, [event?.id])
  const isSeries = Boolean(event && (event.is_recurrence_instance || event.series_id || event.recurrence))
  return (
    <Dialog open={event !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('calendar.cancel.title', locale)}</DialogTitle>
        </DialogHeader>
        {event ? (
          isSeries ? (
            <div className="space-y-[var(--lc-space-sm)]">
              <p className="text-[var(--lc-text-secondary)]" style={{ font: 'var(--lc-type-body-sm)' }}>
                {t('calendar.cancel.seriesBody', locale, { title: event.title })}
              </p>
              <div role="radiogroup" className="space-y-1">
                {(
                  [
                    ['occurrence', 'calendar.cancel.scope.this'],
                    ['series', 'calendar.cancel.scope.series'],
                  ] as const
                ).map(([value, key]) => (
                  <label
                    key={value}
                    className="flex min-h-tap cursor-pointer items-center gap-[var(--lc-space-sm)] rounded-[var(--lc-radius-sm)] px-2 hover:bg-[var(--lc-surface-sunken)]"
                  >
                    <input
                      type="radio"
                      name={`cancel-scope-${event.id}`}
                      checked={scope === value}
                      onChange={() => setScope(value)}
                    />
                    <span style={{ font: 'var(--lc-type-body-sm)' }}>{t(key, locale)}</span>
                  </label>
                ))}
              </div>
            </div>
          ) : (
            <p className="text-[var(--lc-text-secondary)]" style={{ font: 'var(--lc-type-body-sm)' }}>
              {t('calendar.cancel.body', locale, { title: event.title })}
            </p>
          )
        ) : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}>
            {t('calendar.action.cancel', locale)}
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={busy || event === null}
            onClick={() => event && onConfirm(scope)}
          >
            {busy ? <Loader2 className="me-1 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            {t('calendar.cancel.confirm', locale)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function errorMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message
  return String(err)
}

/** Zod validates shape strictly; `required`/`invalid` markers map to localized copy. */
const addMeetingSchema = z.object({
  title: z.string().trim().min(1, 'required'),
  when: z
    .string()
    .min(1, 'required')
    .refine((v) => !Number.isNaN(new Date(v).getTime()), 'invalid'),
  ends: z.string(),
  timezone: z.string(),
  where: z.string(),
  link: z.string(),
  visibility: z.enum(['personal', 'team']),
  description: z.string(),
})

const addTodoSchema = z.object({
  title: z.string().trim().min(1, 'required'),
  when: z
    .string()
    .min(1, 'required')
    .refine((v) => !Number.isNaN(new Date(v).getTime()), 'invalid'),
  description: z.string(),
})

/**
 * §3.4 conflict preflight: probe the chosen slot and surface a non-blocking
 * warning naming the conflicting event. The server stays the authority — this
 * only warns, and preflight failures are swallowed silently.
 *
 * The conflicts route requires from/to with to strictly after from, so a
 * missing/invalid end probes a one-minute window from the start.
 */
function useConflictWarning({
  enabled,
  startInput,
  endInput,
  excludeEventId,
  locale,
}: {
  enabled: boolean
  startInput: string
  endInput: string
  excludeEventId?: string
  locale: DashboardLocale
}): { title: string; time: string } | null {
  const [warning, setWarning] = useState<{ title: string; time: string } | null>(null)

  const startDate = new Date(startInput)
  const startValid = startInput !== '' && !Number.isNaN(startDate.getTime())
  const endDate = new Date(endInput)
  const endValid = endInput !== '' && !Number.isNaN(endDate.getTime()) && endDate > startDate
  const fromISO = startValid ? startDate.toISOString() : null
  const toISO = startValid ? (endValid ? endDate.toISOString() : new Date(startDate.getTime() + 60_000).toISOString()) : null

  useEffect(() => {
    if (!enabled || !fromISO || !toISO) {
      setWarning(null)
      return
    }
    let cancelled = false
    api
      .getCalendarConflicts({ from: fromISO, to: toISO, excludeEventId })
      .then((verdict) => {
        if (cancelled) return
        if (verdict && verdict.ok === false && verdict.code === 'CALENDAR_CONFLICT' && verdict.conflicting) {
          const at = new Date(verdict.conflicting.starts_at)
          setWarning({
            title: verdict.conflicting.title,
            time: Number.isNaN(at.getTime())
              ? ''
              : new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).format(at),
          })
        } else {
          setWarning(null)
        }
      })
      .catch(() => {
        if (!cancelled) setWarning(null)
      })
    return () => {
      cancelled = true
    }
  }, [enabled, fromISO, toISO, excludeEventId, locale])

  return warning
}

function ConflictWarning({ warning, locale }: { warning: { title: string; time: string }; locale: DashboardLocale }) {
  return (
    <p
      data-testid="conflict-warning"
      className="rounded-[var(--lc-radius-sm)] border border-[var(--lc-status-underOffer-fg)] px-2 py-1 text-[var(--lc-status-underOffer-fg)]"
      style={{ font: 'var(--lc-type-caption)' }}
    >
      {t('calendar.conflict.warning', locale, { title: warning.title, time: warning.time })}
    </p>
  )
}

export function RescheduleForm({
  event,
  locale,
  busy,
  onCancel,
  onSubmit,
}: {
  event: CalendarEvent
  locale: DashboardLocale
  busy: boolean
  onCancel: () => void
  onSubmit: (patch: { starts_at: string; ends_at: string | null }) => void
}) {
  const [start, setStart] = useState(() => toLocalInputValue(new Date(event.starts_at)))
  const [end, setEnd] = useState(() => (event.ends_at ? toLocalInputValue(new Date(event.ends_at)) : ''))
  const [error, setError] = useState<string | null>(null)
  const conflict = useConflictWarning({
    enabled: event.kind !== 'todo',
    startInput: start,
    endInput: end,
    excludeEventId: event.id,
    locale,
  })

  const submit = () => {
    const startDate = new Date(start)
    if (start === '' || Number.isNaN(startDate.getTime())) {
      setError(t('calendar.form.invalid', locale))
      return
    }
    let endsAt: string | null = null
    if (event.kind !== 'todo' && end !== '') {
      const endDate = new Date(end)
      if (Number.isNaN(endDate.getTime())) {
        setError(t('calendar.form.invalid', locale))
        return
      }
      if (endDate <= startDate) {
        setError(t('calendar.form.endsAfter', locale))
        return
      }
      endsAt = endDate.toISOString()
    }
    setError(null)
    onSubmit({ starts_at: startDate.toISOString(), ends_at: endsAt })
  }

  return (
    <div className="space-y-[var(--lc-space-md)]">
      <div>
        <Label htmlFor="cal-reschedule-start">{t('calendar.reschedule.start', locale)}</Label>
        <Input
          id="cal-reschedule-start"
          type="datetime-local"
          value={start}
          onChange={(e) => setStart(e.target.value)}
        />
      </div>
      {event.kind !== 'todo' ? (
        <div>
          <Label htmlFor="cal-reschedule-end">
            {t('calendar.reschedule.end', locale)}
            <span className="text-[var(--lc-text-muted)]"> · {t('calendar.reschedule.optional', locale)}</span>
          </Label>
          <Input id="cal-reschedule-end" type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} />
        </div>
      ) : null}
      {error ? (
        <p className="text-[var(--lc-status-unpublished-fg)]" style={{ font: 'var(--lc-type-caption)' }}>
          {error}
        </p>
      ) : null}
      {conflict ? <ConflictWarning warning={conflict} locale={locale} /> : null}
      {event.is_recurrence_instance || event.series_id ? (
        <p
          data-testid="reschedule-series-helper"
          className="text-[var(--lc-text-muted)]"
          style={{ font: 'var(--lc-type-caption)' }}
        >
          {t('calendar.reschedule.seriesHelper', locale)}
        </p>
      ) : null}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
          {t('calendar.action.cancel', locale)}
        </Button>
        <Button type="button" onClick={submit} disabled={busy}>
          {busy ? <Loader2 className="me-1 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
          {t('calendar.form.submit', locale)}
        </Button>
      </DialogFooter>
    </div>
  )
}

export function AddAppointmentDialog({
  open,
  onOpenChange,
  prefill,
  locale,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  prefill: AddPrefill
  locale: DashboardLocale
  onCreated: (event: CalendarEvent) => void
}) {
  const { addToast } = useToast()
  const [kind, setKind] = useState<'meeting' | 'todo'>(prefill?.kind ?? 'meeting')
  const [title, setTitle] = useState('')
  const [when, setWhen] = useState(prefill?.when ?? '')
  const [ends, setEnds] = useState('')
  const [timezone, setTimezone] = useState(resolvedTimezone)
  const [where, setWhere] = useState('')
  const [link, setLink] = useState('')
  const [visibility, setVisibility] = useState<'personal' | 'team'>('personal')
  const [description, setDescription] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  const conflict = useConflictWarning({
    enabled: kind === 'meeting',
    startInput: when,
    endInput: ends,
    locale,
  })

  const submit = async () => {
    if (busy) return
    const localizeIssues = (issues: z.ZodIssue[]) => {
      const errs: Record<string, string> = {}
      for (const issue of issues) {
        const key = String(issue.path[0] ?? 'form')
        if (errs[key]) continue
        errs[key] =
          issue.message === 'required'
            ? t('calendar.form.required', locale)
            : issue.message === 'invalid'
              ? t('calendar.form.invalid', locale)
              : issue.message
      }
      return errs
    }
    const create = async (payload: Parameters<typeof api.createCalendarEvent>[0]) => {
      setBusy(true)
      try {
        const { event } = await api.createCalendarEvent(payload)
        addToast({ title: t('calendar.toast.created', locale), variant: 'success' })
        onCreated(event)
      } catch (err) {
        addToast({
          title: t('calendar.toast.createError', locale),
          description: errorMessage(err),
          variant: 'error',
        })
      } finally {
        setBusy(false)
      }
    }

    if (kind === 'meeting') {
      const parsed = addMeetingSchema.safeParse({
        title,
        when,
        ends,
        timezone,
        where,
        link,
        visibility,
        description,
      })
      if (!parsed.success) {
        setErrors(localizeIssues(parsed.error.issues))
        return
      }
      if (parsed.data.ends && new Date(parsed.data.ends) <= new Date(parsed.data.when)) {
        setErrors({ ends: t('calendar.form.endsAfter', locale) })
        return
      }
      setErrors({})
      await create({
        kind: 'meeting',
        title: parsed.data.title,
        starts_at: new Date(parsed.data.when).toISOString(),
        ends_at: parsed.data.ends ? new Date(parsed.data.ends).toISOString() : null,
        timezone: parsed.data.timezone || resolvedTimezone(),
        location_text: parsed.data.where.trim() || null,
        location_link: parsed.data.link.trim() || null,
        description: parsed.data.description.trim() || null,
        visibility: parsed.data.visibility,
        status: 'confirmed',
        source: 'manual',
      })
      return
    }

    const parsed = addTodoSchema.safeParse({ title, when, description })
    if (!parsed.success) {
      setErrors(localizeIssues(parsed.error.issues))
      return
    }
    setErrors({})
    await create({
      kind: 'todo',
      title: parsed.data.title,
      starts_at: new Date(parsed.data.when).toISOString(),
      ends_at: null,
      timezone: resolvedTimezone(),
      description: parsed.data.description.trim() || null,
      visibility: 'personal',
      status: 'confirmed',
      source: 'manual',
    })
  }

  const errorText = (key: string) =>
    errors[key] ? (
      <p className="mt-1 text-[var(--lc-status-unpublished-fg)]" style={{ font: 'var(--lc-type-caption)' }}>
        {errors[key]}
      </p>
    ) : null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('calendar.add', locale)}</DialogTitle>
        </DialogHeader>
        <div className="space-y-[var(--lc-space-md)]">
          <div
            role="tablist"
            className="inline-flex rounded-[var(--lc-radius-md)] border border-[var(--lc-border)] bg-[var(--lc-surface-sunken)] p-0.5"
          >
            {(
              [
                ['meeting', 'calendar.form.kind.meeting'],
                ['todo', 'calendar.form.kind.todo'],
              ] as const
            ).map(([value, key]) => (
              <button
                key={value}
                type="button"
                role="tab"
                aria-selected={kind === value}
                onClick={() => setKind(value)}
                className={cn(
                  'min-h-tap rounded-[var(--lc-radius-sm)] px-3 transition-colors',
                  kind === value
                    ? 'bg-[var(--lc-surface-raised)] text-[var(--lc-text-primary)] shadow-[var(--lc-elevation-sm)]'
                    : 'text-[var(--lc-text-muted)] hover:text-[var(--lc-text-primary)]',
                )}
                style={{ font: 'var(--lc-type-caption)' }}
              >
                {t(key, locale)}
              </button>
            ))}
          </div>

          <div>
            <Label htmlFor="cal-add-title">{t('calendar.form.title', locale)}</Label>
            <Input
              id="cal-add-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              aria-invalid={Boolean(errors.title)}
            />
            {errorText('title')}
          </div>

          <div>
            <Label htmlFor="cal-add-when">
              {kind === 'meeting' ? t('calendar.form.when', locale) : t('calendar.form.due', locale)}
            </Label>
            <Input
              id="cal-add-when"
              type="datetime-local"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
              aria-invalid={Boolean(errors.when)}
            />
            {errorText('when')}
          </div>

          {kind === 'meeting' ? (
            <>
              <div>
                <Label htmlFor="cal-add-ends">{t('calendar.form.ends', locale)}</Label>
                <Input
                  id="cal-add-ends"
                  type="datetime-local"
                  value={ends}
                  onChange={(e) => setEnds(e.target.value)}
                  aria-invalid={Boolean(errors.ends)}
                />
                {errorText('ends')}
              </div>
              <div>
                <Label htmlFor="cal-add-tz">{t('calendar.form.timezone', locale)}</Label>
                <Input id="cal-add-tz" value={timezone} onChange={(e) => setTimezone(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="cal-add-where">{t('calendar.form.where', locale)}</Label>
                <Input id="cal-add-where" value={where} onChange={(e) => setWhere(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="cal-add-link">{t('calendar.form.link', locale)}</Label>
                <Input
                  id="cal-add-link"
                  type="url"
                  inputMode="url"
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="cal-add-visibility">{t('calendar.form.visibility', locale)}</Label>
                <select
                  id="cal-add-visibility"
                  value={visibility}
                  onChange={(e) => setVisibility(e.target.value as 'personal' | 'team')}
                  className="mt-1 flex min-h-tap w-full rounded-[var(--lc-radius-md)] border border-[var(--lc-border-strong)] bg-[var(--lc-surface)] px-3 py-2 text-sm text-[var(--lc-text-primary)]"
                >
                  <option value="personal">{t('calendar.form.personal', locale)}</option>
                  <option value="team">{t('calendar.form.team', locale)}</option>
                </select>
              </div>
            </>
          ) : null}

          <div>
            <Label htmlFor="cal-add-description">
              {kind === 'meeting' ? t('calendar.form.description', locale) : t('calendar.form.notes', locale)}
            </Label>
            <textarea
              id="cal-add-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="flex w-full rounded-[var(--lc-radius-md)] border border-[var(--lc-border-strong)] bg-[var(--lc-surface)] px-3 py-2 text-sm text-[var(--lc-text-primary)] placeholder:text-[var(--lc-text-muted)] focus-visible:outline-none"
            />
          </div>

          {conflict ? <ConflictWarning warning={conflict} locale={locale} /> : null}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            {t('calendar.action.cancel', locale)}
          </Button>
          <Button type="button" onClick={() => void submit()} disabled={busy}>
            {busy ? <Loader2 className="me-1 h-4 w-4 animate-spin" aria-hidden="true" /> : null}
            {t('calendar.form.submit', locale)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
